import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, signal, Signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

import { APP_BUILD_VERSION } from '../config/version';

const RELOADED_VERSION_STORAGE_KEY = 'idoc.reloadedForVersion';

export interface AppVersionManifest {
  version: string;
  buildTime?: string;
}

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly document: Document = inject(DOCUMENT);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly router: Router = inject(Router);
  private readonly swUpdate: SwUpdate = inject(SwUpdate);

  private readonly updateAvailableState: ReturnType<typeof signal<boolean>> = signal<boolean>(false);
  readonly updateAvailable: Signal<boolean> = this.updateAvailableState.asReadonly();

  private readonly currentVersion: string = APP_BUILD_VERSION;

  private latestVersion: string | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private running: boolean = false;
  private navigationSubscribed: boolean = false;
  private visibilitySubscribed: boolean = false;
  private reloading: boolean = false;
  private lastPath: string = this.pathOf(this.router.url);

  private readonly onVisibilityChange: () => void = (): void => {
    if (this.document.visibilityState === 'visible') {
      void this.check();
    }
  };

  start(intervalMs: number = 5 * 60 * 1000): void {
    if (this.running) {
      return;
    }
    this.running = true;
    this.lastPath = this.pathOf(this.router.url);
    void this.check();
    this.timer = setInterval(() => void this.check(), intervalMs);

    if (!this.navigationSubscribed) {
      this.navigationSubscribed = true;
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => {
          const path = this.pathOf(event.urlAfterRedirects);
          const pathChanged = path !== this.lastPath;
          this.lastPath = path;

          // Смена только query-параметров или якоря не должна перезагружать
          // страницу: на ней может быть незавершённая форма или загрузка.
          if (pathChanged && this.updateAvailableState()) {
            void this.forceReload();
          }
        });
    }

    if (!this.visibilitySubscribed) {
      this.visibilitySubscribed = true;
      this.document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    this.destroyRef.onDestroy(() => this.stop());
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.visibilitySubscribed) {
      this.visibilitySubscribed = false;
      this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.running = false;
  }

  private pathOf(url: string): string {
    return new URL(url, this.document.baseURI).pathname;
  }

  async check(): Promise<boolean> {
    const url = new URL('version.json', this.document.baseURI);
    url.searchParams.set('cb', Date.now().toString());

    try {
      const manifest = await firstValueFrom(this.http.get<AppVersionManifest>(url.toString(), { cache: 'no-store' }));

      if (!manifest?.version) {
        return false;
      }

      if (manifest.version !== this.currentVersion) {
        this.latestVersion = manifest.version;
        this.updateAvailableState.set(true);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  private async forceReload(): Promise<void> {
    if (this.reloading) {
      return;
    }
    this.reloading = true;

    const browserWindow = this.document.defaultView;
    const serviceWorker = browserWindow?.navigator.serviceWorker;

    // Если страницей управляет Service Worker, обновляем его манифест и только
    // затем перезагружаемся: иначе старый воркер отдаст закэшированный index.html
    // и приложение снова запустится со старой версией.
    if (this.swUpdate.isEnabled && serviceWorker?.controller) {
      try {
        await this.swUpdate.checkForUpdate();
        await this.swUpdate.activateUpdate();
      } catch {
        // Ошибка обновления воркера не должна блокировать перезагрузку.
      }
    }

    // Перезагрузка не гарантирует новую сборку: воркер мог ещё не увидеть
    // новый ngsw.json, а activateUpdate() мог упасть. Тогда старое приложение
    // снова пометит обновление и перезагрузится на каждом переходе. Метка в
    // sessionStorage останавливает этот цикл в рамках одной вкладки.
    let alreadyReloaded = false;
    try {
      const storage = browserWindow?.sessionStorage;
      alreadyReloaded = storage?.getItem(RELOADED_VERSION_STORAGE_KEY) === this.latestVersion;
      if (!alreadyReloaded) {
        storage?.setItem(RELOADED_VERSION_STORAGE_KEY, this.latestVersion ?? '');
      }
    } catch {
      // Хранилище недоступно (Safari/privacy, sandboxed iframe, квота): без метки
      // лучше один раз перезагрузиться, чем зависнуть на старой версии.
    }
    if (alreadyReloaded) {
      this.reloading = false;
      return;
    }

    browserWindow?.location.reload();
  }
}
